import { Trip, Traveller, Apartment, SignUpData } from '@/types';
import * as tripRepository from '@/repository/tripRepository';

export interface ComprehensiveTripExport {
  trip: Trip;
  subcollections: {
    travellers: Traveller[];
    apartments: Apartment[];
    signUps: SignUpData[];
  };
  exportTimestamp: string;
  exportVersion: string;
}

export class ExportService {
  /**
   * Exports a comprehensive trip backup including all subcollections
   */
  static async exportTripData(trip: Trip): Promise<ComprehensiveTripExport> {
    if (!trip?.id) {
      throw new Error('Trip ID is required for export');
    }

    // Fetch all subcollections in parallel
    const [travellers, apartments, signUps] = await Promise.all([
      tripRepository.getSubcollection<Traveller>(trip.id, 'travellers'),
      tripRepository.getSubcollection<Apartment>(trip.id, 'apartments'),
      tripRepository.getSubcollection<SignUpData>(trip.id, 'signUps'),
    ]);

    // Create comprehensive export data
    const exportData: ComprehensiveTripExport = {
      trip,
      subcollections: {
        travellers,
        apartments,
        signUps,
      },
      exportTimestamp: new Date().toISOString(),
      exportVersion: '1.0',
    };

    return exportData;
  }

  /**
   * Generates a filename for the export
   */
  static generateExportFilename(trip: Trip): string {
    const date = new Date().toISOString().split('T')[0];
    const safeTripName = trip.name.replace(/[^a-zA-Z0-9]/g, '-');
    return `trip-${safeTripName}-complete-backup-${date}.json`;
  }

  /**
   * Downloads the export data as a JSON file
   */
  static downloadExportData(exportData: ComprehensiveTripExport, filename: string): void {
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', filename);
    linkElement.click();
  }
}